import type { QueryClient } from "@tanstack/react-query";
import { queryOptions, skipToken, useQuery } from "@tanstack/react-query";
import { apiWithoutCamelization } from "../../../api";
import urls from "../../../urls";
import type { Connection } from "../../../utils/backend-types";
import { COMPONENT_QUERY_KEY } from "../../../utils/constants";
import { ObjectName } from "../../../utils/enums";
import { camelizeKeys } from "../../../utils/SnakeCamelConversion";
import type { ConnectionResponse } from "../Connection.types";

export const connectionQueries = {
  list: (params: { siteOrVariantId?: number }) =>
    queryOptions({
      queryKey: [
        COMPONENT_QUERY_KEY[ObjectName.Connection],
        {
          objectName: ObjectName.Connection,
          siteOrVariantId: params.siteOrVariantId
        }
      ]
    }),
  detail: (connectionId?: number) =>
    queryOptions<ConnectionResponse>({
      queryKey: ["connection", { connectionId }],
      queryFn:
        typeof connectionId !== "undefined"
          ? () => fetchConnection(connectionId)
          : skipToken,
      refetchInterval: false,
      refetchOnWindowFocus: false
    })
};

export function useConnection(connectionId?: number) {
  return useQuery(connectionQueries.detail(connectionId));
}

async function fetchConnection(connectionId: number) {
  const response = await apiWithoutCamelization.get<ConnectionResponse>(
    urls.apiWithoutCamelization.connection(connectionId)
  );
  return response.data;
}

/**
 * Appends `connection` to the cached `connectionQueries.list(params)` result
 * so a newly created connection shows up immediately, with no blank-select
 * flash while the list refetches. Skips an uncached list, which would
 * otherwise render as a one-entry select for a frame.
 *
 * `connection` comes from the non-camelized `apiWithoutCamelization` client,
 * while the list cache holds the camelized shape `useConfiguratorComponents`
 * reads via `api` — so it's camelized here to match.
 */
export function mergeConnectionIntoListCache(
  queryClient: QueryClient,
  params: { siteOrVariantId?: number },
  connection: ConnectionResponse
) {
  queryClient.setQueryData(
    connectionQueries.list(params).queryKey,
    (old: Array<Connection> | undefined) =>
      old ? [...old, camelizeKeys(connection)] : old
  );
}
